import type { EffectivePatientField } from '@helios/data-access';
import type { PatientFormStep } from '@helios/domain';
import type { IntakeQuestionnaire, Question, Option, FieldType, Section } from '@helios/ui/intake';

/**
 * Maps field_key → select options for fields that need dropdowns.
 * Populated by the consuming page (e.g., CreatePatientPage).
 */
export type FieldOptionsMap = Record<string, Option[]>;

/**
 * Describes a named section and which field_keys belong to it.
 * Used by buildMultiSectionQuestionnaire to split fields into sections.
 */
export interface SectionConfig {
  id: string;
  title: string;
  fieldKeys: string[];
}

/**
 * Maps PatientFieldType → IntakeRenderer FieldType.
 * Types not in IntakeRenderer's FieldType union get a safe fallback.
 */
function mapFieldType(type: string | undefined): FieldType {
  switch (type) {
    case 'text':
      return 'text';
    case 'textarea':
      return 'textarea';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'select':
      return 'select';
    case 'email':
      return 'text'; // IntakeRenderer has no 'email' type
    case 'tel':
      return 'text'; // IntakeRenderer has no 'tel' type
    case 'checkbox':
      return 'yes-no'; // closest match
    default:
      return 'text';
  }
}

function buildQuestion(field: EffectivePatientField, optionsMap?: FieldOptionsMap): Question {
  const mappedType = mapFieldType(field.field_type);

  const question: Question = {
    id: field.field_key,
    fieldKey: field.field_key,
    label: field.label,
    type: mappedType,
    required: field.required,
    layout: field.width === 'full' ? 'full' : undefined,
  };

  // Attach options for select fields
  if (mappedType === 'select' && optionsMap?.[field.field_key]) {
    question.options = optionsMap[field.field_key];
  }

  return question;
}

/**
 * Converts effective patient fields into an IntakeQuestionnaire for a given step.
 *
 * - Filters to visible fields in the target step
 * - Optionally restricts to a subset of field_keys (for incremental step conversion)
 * - Sorts by order
 * - Maps PatientFieldType → IntakeRenderer FieldType
 * - Attaches select options from the provided map
 */
export function buildStepQuestionnaire(
  effectiveFields: EffectivePatientField[],
  step: PatientFormStep,
  optionsMap?: FieldOptionsMap,
  includeFields?: string[],
): IntakeQuestionnaire {
  const includeSet = includeFields ? new Set(includeFields) : null;
  const visible = effectiveFields
    .filter((f) => f.step === step && f.visible && (!includeSet || includeSet.has(f.field_key)))
    .sort((a, b) => a.order - b.order);

  const questions: Question[] = visible.map((field) => buildQuestion(field, optionsMap));

  return {
    id: `patient-${step.toLowerCase().replace(/\s+/g, '-')}`,
    status: 'published',
    version: 1,
    sections: [
      {
        id: `section-${step.toLowerCase().replace(/\s+/g, '-')}`,
        title: step,
        questions,
        collapsed: false,
      },
    ],
    lastModified: '',
  };
}

/**
 * Builds a multi-section IntakeQuestionnaire for a step.
 * Each SectionConfig describes a titled section with specific field_keys.
 * Sections whose fields are all hidden (via settings) are omitted.
 */
export function buildMultiSectionQuestionnaire(
  effectiveFields: EffectivePatientField[],
  step: PatientFormStep,
  sectionConfigs: SectionConfig[],
  optionsMap?: FieldOptionsMap,
): IntakeQuestionnaire {
  const fieldMap = new Map<string, EffectivePatientField>();
  for (const f of effectiveFields) {
    if (f.step === step && f.visible) {
      fieldMap.set(f.field_key, f);
    }
  }

  const sections: Section[] = [];
  for (const config of sectionConfigs) {
    const questions: Question[] = [];
    for (const key of config.fieldKeys) {
      const field = fieldMap.get(key);
      if (field) {
        questions.push(buildQuestion(field, optionsMap));
      }
    }
    // Omit empty sections (all fields hidden via settings)
    if (questions.length > 0) {
      sections.push({
        id: config.id,
        title: config.title,
        questions,
        collapsed: false,
      });
    }
  }

  return {
    id: `patient-${step.toLowerCase().replace(/\s+/g, '-')}`,
    status: 'published',
    version: 1,
    sections,
    lastModified: '',
  };
}
